import { useState, useCallback } from 'react';
import { playbookApi } from '../api/playbookApi';
import type { WebhookDelivery } from '../types/playbook.types';

export function useWebhookDeliveries(playbookId: string | null) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<Record<string, boolean>>({});

  const load = useCallback(async (params?: { status?: string; from?: string; to?: string; limit?: number; offset?: number }) => {
    if (!playbookId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await playbookApi.listWebhookDeliveries(playbookId, params);
      const data = res as any;
      setDeliveries(data.deliveries || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('[useWebhookDeliveries] Failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to load webhook deliveries');
    } finally {
      setLoading(false);
    }
  }, [playbookId]);

  const retry = useCallback(async (deliveryId: string) => {
    if (!playbookId) return;
    setRetrying((prev) => ({ ...prev, [deliveryId]: true }));
    try {
      await playbookApi.retryWebhookDelivery(playbookId, deliveryId);
      // Refresh list after retry
      await load();
    } catch (err) {
      console.error('[useWebhookDeliveries] Retry failed:', err);
    } finally {
      setRetrying((prev) => ({ ...prev, [deliveryId]: false }));
    }
  }, [playbookId, load]);

  return { deliveries, total, loading, error, retrying, load, retry };
}
